/**
 * Install-prompt availability, as reactive state.
 *
 * `pwa/install.ts`'s `InstallPromptController` captures `beforeinstallprompt`
 * and classifies three states (`installable`, `ios-manual`, `unavailable`).
 * This store is what lets several surfaces share one live answer — the Play
 * tab's install card and its quiet "Install app" button, and the row in
 * Settings — without each owning a pair of `beforeinstallprompt` /
 * `appinstalled` listeners. There is only ever one deferred prompt to hand
 * out, and two controllers racing for it is worth avoiding on principle.
 */
import { InstallPromptController } from '../pwa/install.js';
import type { InstallAvailability } from '../pwa/install.js';

interface InstallState {
  availability: InstallAvailability;
}

export const install: InstallState = $state({ availability: 'unavailable' });

let controller: InstallPromptController | null = null;

/**
 * Starts listening for `beforeinstallprompt`/`appinstalled` and keeps
 * `install.availability` current. Returns a cleanup function — the same
 * shape `state/account.svelte.ts#watchSettings` already has — so
 * `App.svelte` can wire this from one `$effect`, identically:
 * `$effect(() => watchInstall())`.
 *
 * Safe to call more than once: a later call tears down and replaces the
 * earlier controller rather than leaking a second pair of listeners, though
 * `App.svelte` only ever calls this once in practice.
 */
export function watchInstall(): () => void {
  const next = new InstallPromptController({
    onChange: (state) => {
      install.availability = state;
    },
  });
  controller = next;
  install.availability = next.state;
  return () => {
    next.destroy();
    if (controller === next) controller = null;
  };
}

/**
 * Fires the captured browser install prompt. Resolves `'unavailable'` — a
 * safe no-op — when nothing is deferred, including before `watchInstall` has
 * ever run: the `ios-manual` path never calls this at all (there is no
 * `beforeinstallprompt` on iOS to have captured), so callers do not need to
 * guard on `install.availability` themselves before invoking it.
 */
export async function promptInstall(): Promise<'accepted' | 'dismissed' | 'unavailable'> {
  if (controller === null) return 'unavailable';
  return controller.promptInstall();
}
